import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CameraProvider } from "./contexts/CameraContext";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Scan from "./pages/Scan";
import Result from "./pages/Result";
import History from "./pages/History";
import Upgrade from "./pages/Upgrade";
import Profiles from "./pages/Profiles";
import MenuScan from "./pages/MenuScan";
import MenuResult from "./pages/MenuResult";
import Settings from "./pages/Settings";
import Favorites from "./pages/Favorites";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import About from "./pages/About";
import NotFound from "./pages/NotFound";
import PublicScan from "./pages/PublicScan";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";

// Memoized toast options to prevent re-renders
const toastOptions = {
  style: { marginBottom: '80px' },
  className: 'pointer-events-none'
};

// Clean up any stale body classes on app init (fixes black screen after hard refresh)
if (typeof document !== 'undefined') {
  document.body.classList.remove('camera-open');
}

const ProtectedRoute = ({ children, skipOnboardingCheck = false }) => {
  const { token, onboardingCompleted, loading } = useAuth();
  const location = useLocation();
  
  // Ensure body class is removed when entering protected routes
  useEffect(() => {
    document.body.classList.remove('camera-open');
  }, [location.pathname]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-qc-green border-t-transparent" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (!onboardingCompleted && !skipOnboardingCheck && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CameraProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={
              <ProtectedRoute skipOnboardingCheck={true}>
                <Onboarding />
              </ProtectedRoute>
            } />
            <Route path="/home" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="/scan" element={
              <ProtectedRoute>
                <Scan />
              </ProtectedRoute>
            } />
            <Route path="/result/:scanId" element={
              <ProtectedRoute>
                <Result />
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            } />
            <Route path="/upgrade" element={
              <ProtectedRoute>
                <Upgrade />
              </ProtectedRoute>
            } />
            <Route path="/profiles" element={
              <ProtectedRoute>
                <Profiles />
              </ProtectedRoute>
            } />
            <Route path="/menu-scan" element={
              <ProtectedRoute>
                <MenuScan />
              </ProtectedRoute>
            } />
            <Route path="/menu-result/:scanId" element={
              <ProtectedRoute>
                <MenuResult />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            } />
            {/* Public pages (no auth required) */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/s/:scanId" element={<PublicScan />} />
            {/* Default and catch-all routes */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster 
            position="bottom-center" 
            toastOptions={toastOptions}
          />
        </BrowserRouter>
      </CameraProvider>
    </AuthProvider>
  );
}

export default App;
